import { FAN_URL } from '@constants/api.constants';
import { cookieService } from '@lib/cookie.service';
import Rollbar from 'rollbar';

const rollbar = new Rollbar({
  accessToken: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
  captureUncaught: false,
  captureUnhandledRejections: false,
  payload: {
    environment: process.env.NEXT_PUBLIC_ENVIRONMENT || 'development',
  },
  enabled: process.env.NODE_ENV !== 'development',
});

interface RegenerateSEOParams {
  type: 'creator' | 'fan' | 'post' | 'content' | 'community' | 'topic' | 'badge';
  creatorSlug: string;
  identifier?: string;
  force?: boolean;
}

export async function regenerateSEO(params: RegenerateSEOParams): Promise<void> {
  try {
    const { accessToken, refreshToken } = cookieService.getAuthCookies();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (accessToken && refreshToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      headers['Refresh-token'] = refreshToken;
    }

    const response = await fetch(`${FAN_URL}/badge/regenerate`, {
      method: 'POST',
      headers,
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorText = await response.text();
      rollbar.error('SEO regeneration failed', {
        params,
        status: response.status,
        statusText: response.statusText,
        responseBody: errorText,
      });
      return;
    }
  } catch (error) {
    rollbar.error('SEO regeneration error', {
      params,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

export async function regenerateBadgeSEO(badgeId: string, creatorSlug: string): Promise<void> {
  return regenerateSEO({
    type: 'badge',
    creatorSlug,
    identifier: badgeId,
  });
}

export async function regenerateCreatorSEO(creatorSlug: string): Promise<void> {
  return regenerateSEO({
    type: 'creator',
    creatorSlug,
  });
}

export async function regenerateContentSEO(
  creatorSlug: string,
  contentSlug: string,
): Promise<void> {
  return regenerateSEO({
    type: 'content',
    creatorSlug,
    identifier: contentSlug,
  });
}

export async function regeneratePostSEO(creatorSlug: string, postSlug: string): Promise<void> {
  return regenerateSEO({
    type: 'post',
    creatorSlug,
    identifier: postSlug,
  });
}
